// frontend/modules/customer-category/components/CustomerCategoryPageForm.tsx
import React, { useState, useMemo } from 'react';
import {
    Form,
    Input,
    Select,
    Tabs,
    Button,
    Radio,
    message,
    Row,
    Col
} from 'antd';
import {
    SaveOutlined
} from '@ant-design/icons';
import {CustomerCategoryDTO} from "@/shared/api/model";
import CustomerTypeConfigForm from './CustomerTypeConfigForm';
import HierarchicalCustomerCategoryForm from './HierarchicalCustomerCategoryForm';
import CustomerCategoryDocumentRequirements from './CustomerCategoryDocumentRequirements';
import CustomerCategoryLocationRequirements from './CustomerCategoryLocationRequirements';
import ProductSelectionTab from './tabs/ProductSelectionTab';
import FieldConfigurationTab from './tabs/FieldConfigurationTab';
import UserGroupsTab from './tabs/UserGroupsTab';
import { useCustomerCategoryFormState } from '../hooks/useCustomerCategoryFormState';
import { useCustomerCategoryDataLoader } from '../hooks/useCustomerCategoryDataLoader';

const { Option } = Select;
const { TextArea } = Input;

interface CustomerCategoryPageFormProps {
    mode: 'create' | 'edit' | 'view';
    initialData: CustomerCategoryDTO | null;
    onSubmit: (formData: CustomerCategoryDTO) => void;
    onCancel: () => void;
}

const CustomerCategoryPageForm: React.FC<CustomerCategoryPageFormProps> = ({
    mode,
    initialData,
    onSubmit,
    onCancel
}) => {
    const [activeTab, setActiveTab] = useState('1');
    
    // Debug: Track renders
    const renderCount = React.useRef(0);
    renderCount.current += 1;
    console.log(`[CustomerCategoryPageForm] Render #${renderCount.current}`, {
        mode,
        initialDataId: initialData?.id,
        activeTab
    });

    // Use custom hooks for state management and data loading
    const {
        form,
        formState,
        isSubmitting,
        setIsSubmitting,
        updateFormState,
        updateFieldsFromEntityFields,
        updateFieldsFromGroupedConfiguration,
        createGroupedFieldSubmission,
        handleCustomerTypeChange,
        handleAddProduct,
        handleRemoveProduct,
        handleProductPropertyChange,
        handleFieldPropertyChange,
        handleAddUserGroup,
        handleRemoveUserGroup,
        handleGroupPropertyChange
    } = useCustomerCategoryFormState({ mode, initialData });

    const {
        allProducts,
        userGroups,
        entityFields,
        memberTemplateCategories,
        isLoadingProducts,
        isLoadingGroups,
        isLoadingFields,
        isLoadingMainEntities
    } = useCustomerCategoryDataLoader({ customerType: formState.customerType });
    
    // Debug: Track what's changing
    React.useEffect(() => {
        console.log('[CustomerCategoryPageForm] formState changed:', formState);
    }, [formState]);
    
    React.useEffect(() => {
        console.log('[CustomerCategoryPageForm] allProducts changed:', allProducts?.length);
    }, [allProducts]);
    
    React.useEffect(() => {
        console.log('[CustomerCategoryPageForm] userGroups changed:', userGroups?.length);
    }, [userGroups]);
    
    React.useEffect(() => {
        console.log('[CustomerCategoryPageForm] entityFields changed:', entityFields);
    }, [entityFields]);
    
    React.useEffect(() => {
        console.log('[CustomerCategoryPageForm] Callbacks changed:', {
            updateFormState,
            handleCustomerTypeChange,
            handleAddProduct,
            handleRemoveProduct,
            handleProductPropertyChange,
            handleFieldPropertyChange,
            updateFieldsFromGroupedConfiguration,
            handleAddUserGroup,
            handleRemoveUserGroup,
            handleGroupPropertyChange
        });
    }, [
        updateFormState,
        handleCustomerTypeChange,
        handleAddProduct,
        handleRemoveProduct,
        handleProductPropertyChange,
        handleFieldPropertyChange,
        updateFieldsFromGroupedConfiguration,
        handleAddUserGroup,
        handleRemoveUserGroup,
        handleGroupPropertyChange
    ]);

    // Update the form state hook with entity fields when they change
    // Use ref to prevent infinite loop from updateFieldsFromEntityFields
    const updateFieldsFromEntityFieldsRef = React.useRef(updateFieldsFromEntityFields);
    updateFieldsFromEntityFieldsRef.current = updateFieldsFromEntityFields;
    
    const hasInitializedFieldsRef = React.useRef(false);
    
    React.useEffect(() => {
        if (entityFields && mode === 'create' && !hasInitializedFieldsRef.current) {
            hasInitializedFieldsRef.current = true;
            updateFieldsFromEntityFieldsRef.current(entityFields);
        }
    }, [entityFields, mode]); // Remove updateFieldsFromEntityFields from dependencies

    // Memoize the configuration change handler to prevent recreation
    const handleConfigurationChange = React.useCallback((config: any) => {
        console.log('=== CONFIGURATION CHANGE FROM FIELD TAB ===');
        console.log('New Configuration:', config);
        updateFieldsFromGroupedConfiguration(config);
        console.log('=== END CONFIGURATION CHANGE ===');
    }, [updateFieldsFromGroupedConfiguration]);
    
    // Memoize location requirements change handler
    const handleLocationRequirementsChange = React.useCallback((requirements: any) => {
        updateFormState({ locationRequirements: requirements });
    }, [updateFormState]);
    
    // Memoize document requirements change handler
    const handleDocumentRequirementsChange = React.useCallback((requirements: any) => {
        updateFormState({ documentRequirements: requirements });
    }, [updateFormState]);
    
    // Memoize group config change handler
    const handleGroupConfigChange = React.useCallback((config: any) => {
        updateFormState({ groupConfig: config });
    }, [updateFormState]);
    
    // Memoize business config change handler
    const handleBusinessConfigChange = React.useCallback((config: any) => {
        updateFormState({ businessConfig: config });
    }, [updateFormState]);
    
    // Memoize UI configuration change handler
    const handleUIConfigurationChange = React.useCallback((config: any) => {
        updateFormState({ uiConfiguration: config });
    }, [updateFormState]);
    
    // Memoize form data change handler for hierarchical form
    const handleHierarchicalFormDataChange = React.useCallback((data: any) => {
        updateFormState(data);
    }, [updateFormState]);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();
            
            // DEBUG: Log form state before submission
            console.log('=== CUSTOMER CATEGORY FORM SUBMISSION DEBUG ===');
            console.log('Mode:', mode);
            console.log('Customer Type:', formState.customerType);
            console.log('Form Values:', values);
            console.log('Form State:', formState);
            console.log('Category Fields Count:', formState.categoryFields?.length || 0);
            console.log('Category Fields:', formState.categoryFields);
            console.log('Selected Products Count:', formState.selectedProducts?.length || 0);
            console.log('Selected Products:', formState.selectedProducts);
            console.log('Selected Groups Count:', formState.selectedGroups?.length || 0);
            console.log('Selected Groups:', formState.selectedGroups);
            console.log('Document Requirements Count:', formState.documentRequirements?.length || 0);
            console.log('Location Requirements Count:', formState.locationRequirements?.length || 0);
            console.log('Group Config:', formState.groupConfig);
            console.log('Business Config:', formState.businessConfig);
            
            // Create grouped field submission structure
            const groupedFieldConfiguration = createGroupedFieldSubmission();
            console.log('Grouped Field Configuration:', groupedFieldConfiguration);
            
            // Prepare submission data with grouped field structure
            const formData: CustomerCategoryDTO = {
                ...values,
                customerType: formState.customerType,
                isSubPart: formState.isSubPart,
                parentCategoryId: formState.parentCategoryId,
                subPartType: formState.subPartType,
                products: formState.selectedProducts,
                fields: formState.categoryFields, // Keep flat fields for backward compatibility
                groupedFieldConfiguration: groupedFieldConfiguration, // Add grouped structure with UI config
                userGroups: formState.selectedGroups,
                groupConfig: formState.customerType === 'GROUP' && !formState.isSubPart ? formState.groupConfig : null,
                businessConfig: formState.customerType === 'BUSINESS' && !formState.isSubPart ? formState.businessConfig : null,
                uiConfiguration: formState.uiConfiguration, // Add category-level UI configuration
                documents: formState.documentRequirements,
                locations: formState.locationRequirements
            };

            // If editing, include the ID
            if (initialData && initialData.id) {
                formData.id = initialData.id;
            }

            console.log('Final Form Data to Submit:', formData);
            console.log('Fields in Final Form Data (flat):', formData.fields);
            console.log('Grouped Field Configuration:', formData.groupedFieldConfiguration);
            console.log('UI Configuration:', formData.uiConfiguration);
            console.log('=== END SUBMISSION DEBUG ===');

            await onSubmit(formData);
            message.success(`Customer category ${mode === 'create' ? 'created' : 'updated'} successfully`);
        } catch (error) {
            console.error('Error submitting form:', error);
            console.error('Error details:', error);
            message.error(`Error ${mode === 'create' ? 'creating' : 'updating'} customer category`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Customer type change is now handled by the form state hook

    // All management functions are now handled by the form state hook

    // Product tab rendering is now handled by ProductSelectionTab component

    // Fields tab rendering is now handled by FieldConfigurationTab component

    // User groups tab rendering is now handled by UserGroupsTab component

    // Define all available tabs - memoize to prevent recreation on every render
    const allTabItems = useMemo(() => [
        {
            key: '1',
            label: 'Basic Information',
            children: (
                <Form
                    form={form}
                    layout="vertical"
                    disabled={mode === 'view'}
                >
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Category Name"
                                rules={[
                                    { required: true, message: 'Please enter category name' }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[
                                    { required: true, message: 'Please select status' }
                                ]}
                            >
                                <Radio.Group>
                                    <Radio value="ACTIVE">Active</Radio>
                                    <Radio value="INACTIVE">Inactive</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={4} />
                    </Form.Item>

                    <Form.Item
                        name="customerType"
                        label="Customer Type"
                        rules={[
                            { required: true, message: 'Please select customer type' }
                        ]}
                    >
                        <Select
                            value={formState.customerType}
                            onChange={handleCustomerTypeChange}
                            placeholder="Select customer type"
                        >
                            <Option value="INDIVIDUAL">Individual</Option>
                            <Option value="GROUP">Group</Option>
                            <Option value="BUSINESS">Business</Option>
                        </Select>
                    </Form.Item>
                </Form>
            )
        },
        {
            key: '2',
            label: 'Hierarchy & Type',
            children: (
                <HierarchicalCustomerCategoryForm
                    formData={{
                        customerType: formState.customerType,
                        isSubPart: formState.isSubPart,
                        parentCategoryId: formState.parentCategoryId,
                        subPartType: formState.subPartType,
                        groupConfig: formState.groupConfig,
                        businessConfig: formState.businessConfig
                    }}
                    onFormDataChange={handleHierarchicalFormDataChange}
                    memberTemplateCategories={memberTemplateCategories}
                    disabled={mode === 'view'}
                    mode={mode}
                />
            )
        },
        {
            key: '3',
            label: 'Customer Type Config',
            children: (
                <CustomerTypeConfigForm
                    customerType={formState.customerType}
                    groupConfig={formState.groupConfig}
                    businessConfig={formState.businessConfig}
                    uiConfiguration={formState.uiConfiguration}
                    onGroupConfigChange={handleGroupConfigChange}
                    onBusinessConfigChange={handleBusinessConfigChange}
                    onUIConfigurationChange={handleUIConfigurationChange}
                    disabled={mode === 'view'}
                />
            )
        },
        {
            key: '4',
            label: 'Products',
            children: (
                <ProductSelectionTab
                    selectedProducts={formState.selectedProducts}
                    allProducts={allProducts}
                    isLoadingProducts={isLoadingProducts}
                    mode={mode}
                    // @ts-ignore
                    onAddProduct={(glNo) => handleAddProduct(glNo, allProducts)}
                    onRemoveProduct={handleRemoveProduct}
                    onProductPropertyChange={handleProductPropertyChange}
                />
            )
        },
        {
            key: '5',
            label: 'Fields',
            children: (
                <FieldConfigurationTab
                    customerType={formState.customerType}
                    categoryId={initialData?.id}
                    groupedFieldConfiguration={formState.groupedFieldConfiguration}
                    mode={mode}
                    onFieldPropertyChange={handleFieldPropertyChange}
                    onConfigurationChange={handleConfigurationChange}
                />
            )
        },
        {
            key: '6',
            label: 'User Groups',
            children: (
                <UserGroupsTab
                    selectedGroups={formState.selectedGroups}
                    userGroups={userGroups}
                    isLoadingGroups={isLoadingGroups}
                    mode={mode}
                    onAddUserGroup={handleAddUserGroup}
                    onRemoveUserGroup={handleRemoveUserGroup}
                    onGroupPropertyChange={handleGroupPropertyChange}
                />
            )
        },
        {
            key: '7',
            label: 'Document Requirements',
            children: (
                <CustomerCategoryDocumentRequirements
                    categoryId={initialData?.id}
                    documentRequirements={formState.documentRequirements}
                    onDocumentRequirementsChange={handleDocumentRequirementsChange}
                    disabled={mode === 'view'}
                />
            )
        },
        {
            key: '8',
            label: 'Location Requirements',
            children: (
                <CustomerCategoryLocationRequirements
                    categoryId={initialData?.id}
                    locationRequirements={formState.locationRequirements}
                    onLocationRequirementsChange={handleLocationRequirementsChange}
                    disabled={mode === 'view'}
                />
            )
        }
    ], [
        form,
        formState.customerType,
        formState.isSubPart,
        formState.parentCategoryId,
        formState.subPartType,
        formState.groupConfig,
        formState.businessConfig,
        formState.uiConfiguration,
        formState.selectedProducts,
        formState.groupedFieldConfiguration,
        formState.selectedGroups,
        formState.documentRequirements,
        formState.locationRequirements,
        mode,
        initialData?.id,
        memberTemplateCategories,
        allProducts,
        isLoadingProducts,
        userGroups,
        isLoadingGroups,
        // Use memoized handlers instead of individual callbacks
        handleCustomerTypeChange,
        handleAddProduct,
        handleRemoveProduct,
        handleProductPropertyChange,
        handleFieldPropertyChange,
        handleAddUserGroup,
        handleRemoveUserGroup,
        handleGroupPropertyChange,
        handleConfigurationChange,
        handleLocationRequirementsChange,
        handleDocumentRequirementsChange,
        handleGroupConfigChange,
        handleBusinessConfigChange,
        handleUIConfigurationChange,
        handleHierarchicalFormDataChange
    ]);

    // Filter out Products tab for GROUP customer types - memoize this too
    const tabItems = useMemo(() => formState.customerType === 'GROUP'
        ? allTabItems.filter(tab => tab.key !== '4')
        : allTabItems, [formState.customerType, allTabItems]);

    return (
        <div className="space-y-6">
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />

            {mode !== 'view' && (
                <div className="flex justify-end space-x-4 pt-4 border-t">
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSubmit}
                        loading={isSubmitting}
                    >
                        {mode === 'create' ? 'Create Category' : 'Save Changes'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CustomerCategoryPageForm;
